import { UIEvent, useCallback, useEffect, useReducer, useRef, useState } from 'react'
import { ApiError } from 'services/ApiService'
import { ChatMessage, UserChat } from 'interfaces/UserChat'
import UserChatService from 'services/UserChatService'
import ChatMessageForm from 'components/feature/Chat/display/ChatMessageForm'
import styled from 'styled-components'
import ArrowBackIcon from 'assets/icons/back-arrow.svg'
import Spinner from 'components/elements/Spinner'
import { trackEvent } from 'lib/tracking/analytics-service'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducer'
import theme from 'stories/utils/theme'
import { Headline3 } from 'stories/elements/Typography/Text'
import ChatDisplay from '../display/ChatDisplay'
import { ContentPrefixes } from 'lib/constants/ContentPrefixes'
import _useTranslation from 'lib/hooks/_useTranslation'
import { AnalyticsEventChatSend, AnalyticsEventManualChatMessageImpression } from 'lib/constants/AnalyticsEventType'
import { User } from 'interfaces/User'
import { useToggleState } from 'lib/hooks/useToggleState'
import ChatMembersDialog from './ChatMembersDialog'
import { generateDisplayMessages } from 'lib/features/chat'
import ChatInformationDialog from './ChatInformationDialog'
import InfoIcon from 'assets/icons/know_info.svg'
import { toast } from 'react-toastify'
import { ErrorTypes } from 'lib/constants/ErrorTypes'

const Container = styled.div`
    display: grid;
    grid-template-rows: auto auto 1fr;
    grid-gap: 0;
    padding: 10px;
    background-color: var(--white-background-color);
    box-shadow: 0px 0px 21px rgba(61, 62, 64, 0.08);
    border-radius: ${theme.borderRadius.normal};
    @media (max-width: 750px) {
        grid-template-rows: auto auto 1fr;
        border-radius: 0;
    }
`

interface MobileProps {
    isMobile?: boolean
}
const HeaderWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    height: 50px;
    padding: 0 15px;
    h3 {
        margin: 0 -10px;
        font-size: 18px;
        text-align: center;
    }
    svg {
        position: relative;
        top: 3px;
        right: 5px;
        cursor: pointer;
    }
    span {
        cursor: pointer;
    }
    .back {
        margin-top: 10px;
    }
    @media (max-width: 750px) {
        height: 35px;
        .back {
            margin-top: 0;
        }
    }
`

const MessagesWrapper = styled.div<MobileProps>`
    margin: 0 -8px;
    min-height: ${({ isMobile }) => (isMobile ? '500px' : '65vh')};
    max-height: ${({ isMobile }) => (isMobile ? '65vh' : '67.5vh')};
    padding: 5px 10px;
    overflow-y: auto;
    border-top: 1px solid #ccc;
    border-bottom: 1px solid #ccc;

    gap: 3px;
`

interface Props {
    selectedChat: UserChat | undefined
    resetChat?: () => void
}

const UserChatComponent = ({ selectedChat, resetChat }: Props) => {
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
    const [groupMembers, setGroupMembers] = useState<{ users: User[]; totalUsers: number }>()
    const [nextCursor, setNextCursor] = useState<string>()
    const [showGroupMembersDialog, toggleGroupMemberDialog] = useToggleState(false)
    const [showChatInformationDialog, toggleChatInformationDialog] = useToggleState(false)
    const [page, setPage] = useState(0)
    const [loading, setLoading] = useState(false)
    const [stopLoading, setStopLoading] = useState(false)
    const [_, forceUpdate] = useReducer((x) => x + 1, 0)
    const { t: tCommon } = _useTranslation('common')

    const authenticatedKnower = useSelector((state: AppState) => state.knower)
    const messagesWrapperRef = useRef<HTMLDivElement>(null)
    const chatUuid = selectedChat?.uuid

    const scrollToBottom = () => {
        setTimeout(() => {
            messagesWrapperRef.current?.scrollTo(0, messagesWrapperRef.current?.scrollHeight)
        }, 100)
    }
    const scrollToAlmostTop = () => {
        setTimeout(() => {
            messagesWrapperRef.current?.scrollTo(0, 1)
        }, 100)
    }

    //resetting values right after the component mounted
    useEffect(() => {
        setPage(0)
        setStopLoading(false)
        scrollToBottom()
    }, [chatUuid])

    const fetchGroupMembers = useCallback(() => {
        if (!selectedChat || selectedChat.chatType !== 'GROUP') {
            setGroupMembers(undefined)
            return
        }

        UserChatService.getChatMembers(selectedChat.uuid)
            .then((members) => setGroupMembers(members))
            .catch((error) => {
                if (error instanceof ApiError) {
                    error.handleUnknownError(tCommon, 'userChat.getChatMembers')
                } else {
                    throw error
                }
            })
    }, [selectedChat])

    useEffect(() => {
        fetchGroupMembers()
    }, [fetchGroupMembers])

    const handleManualChatMessageTracking = (messages: ChatMessage[], chat: UserChat) => {
        messages.forEach((message) => {
            if (message && message.manualChatMessageUuid) {
                trackEvent(AnalyticsEventManualChatMessageImpression, {
                    value: message.manualChatMessageUuid,
                    chat: chat,
                    chatMessage: message,
                    chatPartnerUuid: chat?.chatPartner ? chat.chatPartner.uuid : null,
                })
            }
        })
    }

    useEffect(() => {
        chatMessages && selectedChat && handleManualChatMessageTracking(chatMessages, selectedChat)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatMessages])

    const fetchData = useCallback(
        (cursor?: string) => {
            setLoading(true)
            if (!chatUuid) return

            UserChatService.getChatMessages(chatUuid, cursor)
                .then(({ messages, nextCursor }) => {
                    if (messages.length === 0) {
                        setLoading(false)
                        setChatMessages([])
                        setNextCursor(undefined)
                        return
                    }

                    if (messages.length < 20) {
                        setStopLoading(true)
                    }
                    const reverseMessages = messages.reverse()
                    if (!cursor) {
                        setChatMessages(reverseMessages)
                        scrollToBottom()
                    } else {
                        scrollToAlmostTop()
                        setChatMessages((prevMessages) => [...reverseMessages, ...prevMessages])
                    }
                    setNextCursor(nextCursor)
                    setLoading(false)
                })
                .catch((error) => {
                    if (error instanceof ApiError) {
                        error.handleUnknownError(tCommon, 'userChat.getChatMessages')
                    } else {
                        throw error
                    }
                })
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [chatUuid, tCommon]
    )

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatUuid])

    const handleScroll = (event: UIEvent<HTMLDivElement>) => {
        const { scrollTop } = event.currentTarget

        if (scrollTop === 0 && !stopLoading) {
            setPage((prev) => prev + 1)
            fetchData(nextCursor)
        }
    }

    useEffect(() => {
        if (page > 0) return
        const interval = setInterval(() => {
            setPage(0)
            setStopLoading(false)
            setNextCursor(undefined)
            //since setPage(0) wouldn't start rerender if page is already 0 then we add this forceUpdate workaround
            // adding the _ value in dependecy arrow of fetchData. this is temporary fix until websockets
            forceUpdate()
        }, 15000)

        return () => {
            clearInterval(interval)
        }
        // resetting the interval if user scrolled to the top
    }, [chatMessages, page])

    const handleSendMessage = (message: string, filename: string | null, knowUuid: string | null) => {
        if (!chatUuid) {
            return
        }

        if (message.length > 1000) {
            toast.warning(tCommon('common/apiErrorHandlerMaxMessageCharacterLength'))
            return
        }

        const data = {
            message,
            knowUuid: knowUuid ?? null,
            attachmentPath: filename,
        }

        UserChatService.postMessage(chatUuid, data)
            .then((newMessage) => {
                trackEvent(AnalyticsEventChatSend, {
                    value: chatUuid,
                    chat: selectedChat,
                    chatMessage: newMessage,
                    chatPartnerUuid: selectedChat?.chatPartner ? selectedChat?.chatPartner.uuid : null,
                })
                setChatMessages((prevMessages) => (prevMessages ? [...prevMessages, newMessage] : [newMessage]))
                scrollToBottom()
            })
            .catch((error) => {
                if (error instanceof ApiError) {
                    if (error.type === ErrorTypes.FormValidation) {
                        toast.error(tCommon('common/apiErrorHandlerMaxMessageCharacterLength'))
                    } else {
                        error.handleUnknownError(tCommon, 'userChat.postMessage')
                    }
                } else {
                    throw error
                }
            })
    }

    return (
        <Container>
            <HeaderWrapper>
                {resetChat ? <ArrowBackIcon onClick={resetChat} className="back" /> : null}
                <Headline3 color={theme.colors.headlineDark}>
                    {selectedChat?.chatPartner?.username ?? selectedChat?.name ?? 'Chat'}
                    <span onClick={toggleGroupMemberDialog}>
                        {groupMembers?.users
                            ? ` (${groupMembers.users.length}${groupMembers.users.length === 20 ? '+' : ''} members)`
                            : ''}
                    </span>
                </Headline3>
                <InfoIcon onClick={toggleChatInformationDialog} className="back" />
            </HeaderWrapper>
            <MessagesWrapper ref={messagesWrapperRef} onScroll={handleScroll} isMobile={!!resetChat}>
                {loading ? <Spinner forceShow /> : null}
                <ChatDisplay
                    chat={selectedChat}
                    chatMessages={generateDisplayMessages(chatMessages)}
                    ownIdentification={authenticatedKnower?.user.uuid}
                    groupMembers={groupMembers?.users}
                    fetchData={fetchData}
                />
            </MessagesWrapper>
            <ChatMessageForm onSendMessage={handleSendMessage} contentPrefix={ContentPrefixes.ChatAttachment} />
            {groupMembers ? (
                <ChatMembersDialog
                    open={showGroupMembersDialog}
                    onClose={toggleGroupMemberDialog}
                    groupMembers={groupMembers.users}
                />
            ) : null}
            {showChatInformationDialog && selectedChat ? (
                <ChatInformationDialog
                    chat={selectedChat}
                    open={showChatInformationDialog}
                    onClose={toggleChatInformationDialog}
                />
            ) : null}
        </Container>
    )
}

export default UserChatComponent
